<template>
  <v-container fluid class="pa-6">
    <!-- 错误提示 -->
    <div v-if="error" class="mb-4 text-red-500" style="text-align: center;margin: 0 auto;">
      {{ error }}
    </div>

    <!-- 加载提示 -->
    <div v-if="loading" class="flex justify-center p-4" style="text-align: center;margin: 0 auto;">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>

    <!-- 图纸展示区域 -->
    <div class="pb-4">
      <div v-if="!loading && drawsData.length === 0" class="text-center py-8">
        <img 
          src="@/assets/noimage.png" 
          alt="暂无图片" 
          class="no-image"
        />
      </div>

      <DrawingList
        v-else-if="!loading && drawsData.length > 0"
        :drawings="drawsData"
        :selected-states="selectedStates"
        :show-actions="true"
        @image-click="handleImageClick"
        @toggle-select="handleToggleSelect"
        @delete="handleDelete"
        @replace="handleReplace"
      />
    </div>
    <!-- CAD文件显示区域 -->
    <!-- <div class="pb-4">
      <CadFileList
        :files="dwgsData"
        :loading="loading"
        mode='edit'
        @download="downloadDwg"
        @view="viewDwg"
        @delete="deleteDwg"
        @replace="replaceDwg"
      />
    </div> -->
    <!-- 操作日志 -->
    <div class="mt-4" style="padding-bottom: 60px;">
     <OperationLog
       ref="operationLog"
       :product-order-id="Number(contractId)"
       :type="0"
       @error="error = $event"
     />
    </div>

    <!-- 底部固定按钮栏 -->
    <div class="bottom-actions">
      <v-btn 
        variant="flat"
        color="primary"
        prepend-icon="mdi-upload"
        @click="() => {
          uploadMode = 'new'
          uploadDialogOpen = true
        }"
        class="upload-button"
      >
        上传图纸
      </v-btn>
      <v-btn 
        :disabled="!Object.values(selectedStates).some(checked => checked)"
        variant="flat"
        color="primary"
        prepend-icon="mdi-printer"
        @click="handlePrintClick(true)"
        class="print-button"
      >
        打印选中({{ selectedCount }})
      </v-btn>
      <v-btn 
        :disabled="drawsData.length === 0"
        variant="flat"
        color="primary"
        prepend-icon="mdi-printer-outline"
        @click="handlePrintClick(false)"
        class="print-all-button"
      >
        打印全部({{ drawsData.length }})
      </v-btn>
      <v-btn 
        :disabled="drawsData.length === 0"
        variant="flat"
        color="primary"
        prepend-icon="mdi-file-pdf-box"
        @click="handleExportPDFClick"
        class="export-pdf-button"
      >
        导出PDF({{ selectedCount > 0 ? selectedCount : drawsData.length }})
      </v-btn>
    </div>

    <!-- 上传对话框 -->
    <UploadDialog
      v-model="uploadDialogOpen"
      :mode="uploadMode"
      :type="uploadType"
      @upload="handleUpload"
      @update:files="files => uploadFiles = files"
    />

    <!-- 图片预览对话框 -->
    <ImagePreviewDialog
      v-model="showPreview"
      :image-url="previewImage"
      @close="handleClosePreview"
    />

    <!-- 删除确认对话框 -->
    <v-dialog v-model="deleteConfirmOpen" max-width="400px">
      <v-card color="#2B2D33">
        <v-card-title class="text-white">确认删除</v-card-title>
        <v-card-text class="text-white">确定要删除这张图片吗？此操作不可恢复。</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="flat" @click="deleteConfirmOpen = false">取消</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">确认</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DWG删除确认对话框 -->
    <v-dialog v-model="deleteDwgConfirmOpen" max-width="400px">
      <v-card color="#2B2D33">
        <v-card-title class="text-white">确认删除</v-card-title>
        <v-card-text class="text-white">确定要删除这个CAD文件吗？此操作不可恢复。</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="flat" @click="deleteDwgConfirmOpen = false">取消</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteDwg">确认</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 导出PDF对话框 -->
    <ExportPDFDialog
      v-model="exportPDFDialogOpen"
      :has-images="drawsData.length > 0"
      :has-selected-images="selectedCount > 0"
      :total-count="drawsData.length"
      :selected-count="selectedCount"
      @export="handleExportPDF"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DrawingList from '@/components/DrawingList.vue'
import ImagePreviewDialog from '@/components/ImagePreviewDialog.vue'
import UploadDialog from '@/components/UploadDialog.vue'
import OperationLog from '@/components/OperationLog.vue'
import ExportPDFDialog from '@/components/ExportPDFDialog.vue'
import CadFileList from '@/components/CadFileList.vue'
import { orderApi, drawingApi, uploadApi } from '@/services/api'
import { handlePrint } from '@/utils/print'
import { useOrderStore } from '@/stores/order'

const router = useRouter()
const route = useRoute()
const operationLog = ref()
const orderStore = useOrderStore()

// 状态
const loading = ref(false)
const error = ref<string | null>(null)
const drawsData = ref<string[]>([])
const dwgsData = ref<any[]>([]) //cad文件
const selectedStates = ref<Record<number, boolean>>({})
const uploadDialogOpen = ref(false)
const uploadMode = ref<'new' | 'replace'>('new')
const uploadType = ref<'image' | 'dwg'>('image')
const uploadFiles = ref<File[]>([])
const showPreview = ref(false)
const previewImage = ref('')
const deleteConfirmOpen = ref(false)
const deleteDwgConfirmOpen = ref(false)
const exportPDFDialogOpen = ref(false)
const currentImageIndex = ref<number | null>(null)
const currentDwgIndex = ref<number | null>(null)
const orderData = ref<any>(null)

// 计算属性
const contractId = computed(() => route.params.contractId)
const selectedCount = computed(() => 
  Object.values(selectedStates.value).filter(checked => checked).length
)

// 获取数据
const fetchData = async () => {
  if (!contractId.value) {
    error.value = '订单ID不能为空'
    return
  }
  
  loading.value = true
  error.value = null
  
  try { 
    // 从store中获取订单数据
    if (!orderStore.orderData) { 
      await orderStore.fetchOrderData(Number(contractId.value))
    }  
    drawsData.value = JSON.parse(orderStore.orderData.drawsHeader) 
    dwgsData.value = await uploadApi.getFilesByUrls(JSON.parse(orderStore.orderData.dwgsHeader))
    console.log('dwgsData:',dwgsData.value)
    //const res = await drawingApi.getList(Number(contractId.value))
     
    // 初始化选中状态
    selectedStates.value = {}
    drawsData.value.forEach((_, index) => {
      selectedStates.value[index] = false
    })
  } catch (err: any) {
    error.value = err.message || '获取图纸列表失败'
    drawsData.value = []
  } finally {
    loading.value = false
  }
}

// 获取订单数据
const fetchOrderData = async () => {
  const contractId = route.params.contractId
  if (!contractId) return

  try {
    // 使用store中的数据
    if (!orderStore.orderData) {
      await orderStore.fetchOrderData(Number(contractId))
    }
    orderData.value = orderStore.orderData
  } catch (error) {
    console.error('获取订单数据失败:', error)
  }
  fetchData()
}

// 监听路由变化
watch(() => route.params.contractId, fetchOrderData, { immediate: true }) 

// 刷新日志
const refreshLog = async () => {
  await operationLog.value?.fetchLogs()
}

// 处理图片选择
const handleToggleSelect = (index: number, checked: boolean) => {
  selectedStates.value[index] = checked
}

// 处理单击选中
const handleImageClick = (index: number) => {
  previewImage.value = drawsData.value[index]
  showPreview.value = true
}

// 关闭预览
const handleClosePreview = () => {
  showPreview.value = false
  previewImage.value = ''
}

// 处理删除
const handleDelete = (index: number) => {
  currentImageIndex.value = index
  deleteConfirmOpen.value = true
}

// 处理打印
const handlePrintClick = (selectedOnly: boolean) => {
  const imagesToPrint = selectedOnly
    ? drawsData.value.filter((_, index) => selectedStates.value[index])
    : drawsData.value

  handlePrint(imagesToPrint, false)
}

// 处理导出PDF按钮点击
const handleExportPDFClick = () => {
  exportPDFDialogOpen.value = true
}

// 处理导出PDF
const handleExportPDF = (selectedOnly: boolean, orientation: 'portrait' | 'landscape') => {
  console.log('导出PDF', { selectedOnly, orientation });
  const imagesToExport = selectedOnly
    ? drawsData.value.filter((_, index) => selectedStates.value[index])
    : drawsData.value;
  console.log('导出图片', imagesToExport);
  
  // 使用store中的数据
  const fileName = orderStore.orderData?.so?.toString() || '';
  handlePrint(imagesToExport, true, orientation, fileName);
}

// 确认删除
async function confirmDelete() {
  if (currentImageIndex.value === null) return

  try {
    const imageUrl = drawsData.value[currentImageIndex.value]
    const res = await drawingApi.delete({
      productOrderId: Number(contractId.value),
      type: 0,
      img: imageUrl
    })

    if (res?.code === 0) {
      drawsData.value = drawsData.value.filter((_, index) => index !== currentImageIndex.value)
      // 重置选中状态
      selectedStates.value = {}
      drawsData.value.forEach((_, index) => {
        selectedStates.value[index] = false
      })
      // 重新获取最新数据
      //await fetchData()
      // 刷新日志
      await refreshLog()
    } else {
      error.value = res?.msg || '删除失败'
    }
  } catch (err: any) {
    error.value = err.message
  } finally {
    deleteConfirmOpen.value = false
    currentImageIndex.value = null
  }
}

// 处理替换
function handleReplace(index: number) {
  uploadMode.value = 'replace'
  uploadType.value = 'image'
  uploadDialogOpen.value = true
  currentImageIndex.value = index
}

// 处理上传
async function handleUpload(urls: string[]) {
  if (!urls.length) return 
  try {
    if (uploadMode.value === 'replace'){
      if(urls[0].includes('dwg') && currentDwgIndex.value !== null){
        // 替换CAD文件
        const res = await drawingApi.replace({
          productOrderId: Number(contractId.value),
          type: 0,
          img: urls[0],
          target: dwgsData.value[currentDwgIndex.value].url
        })
        let files = await uploadApi.getFilesByUrls(urls)
          dwgsData.value[currentDwgIndex.value] = files[0]
           
      }else if (currentImageIndex.value !== null) {
        // 替换图片
        const res = await drawingApi.replace({
          productOrderId: Number(contractId.value),
          type: 0,
          img: urls[0],
          target: drawsData.value[currentImageIndex.value]
        })
        drawsData.value[currentImageIndex.value] = urls[0]
        
      }
      // 刷新日志
      await refreshLog()
    }else{
         // 新增
        const res = await drawingApi.upload(
          Number(contractId.value), 
          urls
        )
      
        if (res.code === 0) {
          urls.forEach(async(url: string) => {
             if(url.includes('dwg')){
              //新增
              let files = await uploadApi.getFilesByUrls([url])
              files.forEach((file: any) => {
                dwgsData.value.push(file)
              })
             }else{
              //新增图片
              drawsData.value.push(url)
             }
          })
          
          // 重新获取最新数据
          //await fetchData()
          // 刷新日志
          await refreshLog()
        } else {
          error.value = res.msg || '上传图纸失败'
        }
    }
    // if(urls[0].includes('dwg')){
    //   if (uploadMode.value === 'replace' && currentDwgIndex.value !== null) {
    //     // 替换dwg
    //     const res = await drawingApi.replace({
    //       productOrderId: Number(contractId.value),
    //       type: 0,
    //       img: urls[0],
    //       target: dwgsData.value[currentDwgIndex.value].url
    //     })

    //     if (res.code === 0) {
    //       let files = await uploadApi.getFilesByUrls(urls)
    //       dwgsData.value[currentDwgIndex.value] = files[0]
    //       // 刷新日志
    //       await refreshLog()
    //     } else {
    //       error.value = res.msg || '替换失败'
    //     }
    //   } else {
    //     // 新增dwg
    //     try {
    //       for (const url of urls) {
    //         const res = await drawingApi.upload(Number(contractId.value), [url])
    //         if (res.code !== 0) {
    //           error.value = res.msg || '上传失败'
    //           return
    //         }
    //       }
    //       let files = await uploadApi.getFilesByUrls(urls)
    //       files.forEach((file: any) => {
    //         dwgsData.value.push(file)
    //       })
    //       // 所有图片上传成功后，重新获取数据
    //       //await fetchOrderData()
    //       //await fetchData()
    //       // 刷新日志
    //       await refreshLog()

    //     } catch (err: any) {
    //       error.value = err.message || '上传失败'
    //     }
    //   }
    // }else{
    //   if (uploadMode.value === 'replace' && currentImageIndex.value !== null) {
    //     // 替换图片
    //     const res = await drawingApi.replace({
    //       productOrderId: Number(contractId.value),
    //       type: 0,
    //       img: urls[0],
    //       target: drawsData.value[currentImageIndex.value]
    //     })

    //     if (res.code === 0) {
    //       drawsData.value[currentImageIndex.value] = urls[0]
    //       // 刷新日志
    //       await refreshLog()
    //     } else {
    //       error.value = res.msg || '替换失败'
    //     }
    //   } else {
    //     // 新增图片
    //     try {
    //       for (const url of urls) {
    //         const res = await drawingApi.upload(Number(contractId.value), [url])
    //         if (res.code !== 0) {
    //           error.value = res.msg || '上传失败'
    //           return
    //         }
    //         drawsData.value.push(url)
    //       } 
    //       // 所有图片上传成功后，重新获取数据
    //       //await fetchOrderData()
    //       //await fetchData()
    //       // 刷新日志
    //       await refreshLog()

    //     } catch (err: any) {
    //       error.value = err.message || '上传失败'
    //     }
      // }
    //} 
  } catch (err: any) {
    error.value = err.message
  } finally {
    uploadDialogOpen.value = false
    currentImageIndex.value = null
  }
}

// 查看CAD文件
const viewDwg = (file: any) => {
  console.log('查看CAD文件', file)
  window.open('http://cad2.jiangfengoa.com?dwg='+file.url, '_blank')
}

// 删除CAD文件
const deleteDwg = (index: number) => {
  console.log('删除CAD文件', index) 
  deleteDwgConfirmOpen.value = true
  currentDwgIndex.value = index
}

// 下载CAD文件
const downloadDwg = (file: any) => {
  console.log('下载CAD文件', file)
  // 设置下载文件名
  // 使用fetch获取文件内容并创建blob下载
  fetch(file.url)
    .then(res => res.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.originName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    })
    .catch(err => {
      console.error('下载失败:', err)
    })
}

// 替换CAD文件
const replaceDwg = (index: number) => {
  console.log('替换CAD文件', index)
  uploadMode.value = 'replace'
  uploadType.value = 'dwg'
  uploadDialogOpen.value = true
  currentDwgIndex.value = index
   
}

// 确认删除CAD文件
async function confirmDeleteDwg() {
  if (currentDwgIndex.value === null) return
 
  console.log('确认删除CAD文件', currentDwgIndex.value)
  if (currentDwgIndex.value === null) return
  
  try {
    const imageUrl = dwgsData.value[currentDwgIndex.value].url
    const res = await drawingApi.delete({
      productOrderId: Number(contractId.value),
      type: 0,
      img: imageUrl
    })

    if (res?.code === 0) {
      dwgsData.value.splice(currentDwgIndex.value, 1)
      // 重新获取最新数据
      // await fetchOrderData()
      // await fetchData()
      // 刷新日志
      await refreshLog()
    } else {
      error.value = res?.msg || '删除失败'
    }
  } catch (err: any) {
    error.value = err.message
  } finally {
    deleteDwgConfirmOpen.value = false
    currentDwgIndex.value = null
  }
}
// 生命周期
onMounted(() => {
  if (!localStorage.getItem('token')) {
    router.push('/login')
    return
  }
  
  //fetchData()
  //fetchOrderData()
})
</script>

<style lang="scss" scoped>
.bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background-color: rgba(43, 45, 51, 0.7);
  backdrop-filter: blur(10px);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.upload-button,
.print-button,
.print-all-button,
.export-pdf-button {
  min-width: 120px;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.upload-button:not(:disabled):hover,
.print-button:not(:disabled):hover,
.print-all-button:not(:disabled):hover,
.export-pdf-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* 为底部按钮区域留出空间 */
.pb-4 {
  padding-bottom: 80px;
}

.image-preview-container {
  width: 100%;
  height: calc(100vh - 64px);
}

.no-image {
  width: 300px;
  height: auto;
  opacity: 0.5;
}
</style>